import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import type { ComposantEcran } from '../../app/ecrans.js';
import { CIBLE_MIN, COULEURS } from '../../app/styles.js';
import type { Modele, PersonneEquipe } from './dependances.js';
import { messageErreur } from './erreurs.js';
import { construireFiche, envoyerModele, MESSAGE_NOMS_INCONNUS, modelesEnvoyables, type FicheRapide } from './fiche-rapide.js';
import { obtenirServices } from './services.js';

export interface PropsVueFicheRapide {
  fiche: FicheRapide;
  modeles: readonly Modele[];
  choisi: string | null;
  corps: string;
  avertissement: string | null;
  envoye: boolean;
  /** Garde RG-02 : absente ou « chargement », l'envoi reste fermé. */
  verificationMandant?: 'chargement' | 'ok' | 'echec';
  onChoisir: (code: string) => void;
  onEnvoyer: () => void;
  onCorps?: (corps: string) => void;
  titre?: string;
}

const base: CSSProperties = {
  minHeight: CIBLE_MIN,
  minWidth: CIBLE_MIN,
  padding: '0 16px',
  borderRadius: 8,
  border: `1px solid ${COULEURS.marine}`,
  background: COULEURS.blanc,
  color: COULEURS.marine,
  font: 'inherit',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  textDecoration: 'none',
};

function Contact({ libelle, href }: { libelle: string; href: string | null }) {
  return href ? (
    <a href={href} style={base}>
      {libelle}
    </a>
  ) : (
    <span aria-disabled="true" style={{ ...base, color: COULEURS.ardoise, borderColor: COULEURS.bordure, cursor: 'default' }}>
      {libelle}
      {' (indisponible)'}
    </span>
  );
}

const canal = { whatsapp: 'WhatsApp', email: 'e-mail', postal: 'courrier' } as const;

export function VueFicheRapide({
  fiche,
  modeles,
  choisi,
  corps,
  avertissement,
  envoye,
  verificationMandant = 'chargement',
  onChoisir,
  onEnvoyer,
  onCorps,
  titre,
}: PropsVueFicheRapide) {
  const inactif = choisi === null || corps.trim() === '' || verificationMandant === 'chargement';
  return (
    <section data-ecran="M03" aria-labelledby="titre-fiche" style={{ color: COULEURS.marineFonce }}>
      <p style={{ margin: 0, color: COULEURS.ardoise }}>{titre ?? 'Fiche rapide'}</p>
      <h1 id="titre-fiche" style={{ color: COULEURS.marine, fontSize: 28, margin: '4px 0' }}>
        {fiche.nom}
      </h1>
      <p style={{ marginTop: 0 }}>{fiche.sousTitre}</p>
      <p style={{ color: COULEURS.ardoise }}>Fiche rapide : joindre la personne ou lui envoyer un modèle validé.</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 24 }}>
        {Contact({ libelle: 'Appeler', href: fiche.appeler })}
        {Contact({ libelle: 'WhatsApp', href: fiche.whatsapp })}
        {Contact({ libelle: 'E-mail', href: fiche.email })}
      </div>
      <h2 style={{ fontSize: 20 }}>Envoyer un modèle</h2>
      {modeles.length === 0 ? (
        <p>Aucun modèle validé n’est disponible pour la famille.</p>
      ) : (
        <div role="group" aria-label="Modèles disponibles" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
          {modeles.map((m) => (
            <button
              key={m.code}
              type="button"
              aria-pressed={m.code === choisi}
              style={m.code === choisi ? { ...base, background: COULEURS.marine, color: COULEURS.blanc } : base}
              onClick={() => onChoisir(m.code)}
            >
              {`${m.code} (${canal[m.channel]})`}
            </button>
          ))}
        </div>
      )}
      <label style={{ display: 'block', marginBottom: 12 }}>
        Message à relire avant l’envoi
        <textarea
          value={corps}
          readOnly={!onCorps}
          onChange={(e) => onCorps?.(e.target.value)}
          rows={5}
          style={{
            display: 'block',
            minHeight: CIBLE_MIN,
            width: '100%',
            boxSizing: 'border-box',
            padding: 12,
            font: 'inherit',
            borderRadius: 8,
            border: `1px solid ${COULEURS.bordure}`,
          }}
        />
      </label>
      {avertissement ? (
        <p role="alert" style={{ color: COULEURS.rouge }}>
          {avertissement}
        </p>
      ) : null}
      {verificationMandant === 'echec' && !avertissement ? (
        <p role="alert" style={{ color: COULEURS.rouge }}>
          {MESSAGE_NOMS_INCONNUS}
        </p>
      ) : null}
      {verificationMandant === 'chargement' && choisi !== null ? (
        <p role="status" style={{ color: COULEURS.ardoise }}>
          Vérification des noms à protéger en cours. L’envoi sera possible dans un instant.
        </p>
      ) : null}
      {envoye ? <p role="status">Message envoyé. Cabinet MAAT</p> : null}
      <button
        type="button"
        disabled={inactif}
        style={{ ...base, background: COULEURS.boutonPrincipal, color: COULEURS.blanc, border: 'none', opacity: inactif ? 0.5 : 1 }}
        onClick={onEnvoyer}
      >
        Envoyer le message
      </button>
    </section>
  );
}

const FICHE_VIDE: FicheRapide = { nom: 'Chargement de la fiche…', sousTitre: '', appeler: null, whatsapp: null, email: null };

/** Remplit les variables que l'écran connaît ; les autres restent entre accolades et bloquent l'envoi (garde de la fiche). */
function remplir(modele: Modele, personne: PersonneEquipe): string {
  const connues: Record<string, string> = { prenom: personne.firstNames.split(' ')[0] ?? '', signature: 'Cabinet MAAT' };
  return modele.body.replace(/\{([a-z_]+)\}/gi, (tout, nom: string) => connues[nom] ?? tout);
}

export const FicheRapideEcran: ComposantEcran = ({ ecran }) => {
  const services = obtenirServices();
  const parametres = useMemo(() => new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search), []);
  const [personne, setPersonne] = useState<PersonneEquipe | null>(null);
  const [modeles, setModeles] = useState<Modele[]>([]);
  const [nomsMandant, setNomsMandant] = useState<string[]>([]);
  // Garde RG-02 fermée par défaut : tant que la lecture n'a pas abouti, l'envoi est impossible.
  const [verification, setVerification] = useState<'chargement' | 'ok' | 'echec'>('chargement');
  const [compte, setCompte] = useState<string | null>(null);
  const [choisi, setChoisi] = useState<string | null>(null);
  const [corps, setCorps] = useState('');
  const [avertissement, setAvertissement] = useState<string | null>(null);
  const [envoye, setEnvoye] = useState(false);

  useEffect(() => {
    let actif = true;
    const personId = parametres.get('personne');
    services.client
      .appeler<Modele[]>('modeles.lister')
      .then((m) => actif && setModeles(modelesEnvoyables(m)))
      .catch((e: unknown) => actif && setAvertissement(messageErreur(e)));
    if (personId) {
      services.client
        .appeler<PersonneEquipe>('genealogie.personne.lire', { parametres: { personId } })
        .then((p) => {
          if (!actif) return;
          setPersonne(p);
          // Défense en profondeur de RG-02 : les noms du mandant du dossier sont connus avant tout envoi.
          services.client
            .appeler<{ nodes: { role: string; displayName: string }[] }>('genealogie.arbre', { parametres: { dossierId: p.dossierId } })
            .then((a) => {
              if (!actif) return;
              setNomsMandant(a.nodes.filter((n) => n.role === 'mandant').map((n) => n.displayName.split(' ')[0] ?? ''));
              setVerification('ok');
            })
            .catch(() => actif && setVerification('echec'));
          services.client
            .appeler<{ items: { personId: string | null; channelAccount: { id: string } }[] }>('messagerie.fils', {
              requete: { dossierId: p.dossierId },
            })
            .then(
              (f) =>
                actif && setCompte(f.items.find((t) => t.personId === p.id)?.channelAccount.id ?? f.items[0]?.channelAccount.id ?? null),
            )
            .catch(() => undefined);
        })
        .catch((e: unknown) => actif && setAvertissement(messageErreur(e)));
    }
    return () => {
      actif = false;
    };
  }, [services, parametres]);

  const choisir = (code: string) => {
    setChoisi(code);
    setEnvoye(false);
    setAvertissement(null);
    const modele = modeles.find((m) => m.code === code);
    if (modele && personne) setCorps(remplir(modele, personne));
  };

  const envoyer = () => {
    const modele = modeles.find((m) => m.code === choisi);
    if (!modele || !personne || verification === 'chargement') return;
    if (!compte) {
      setAvertissement('Aucun compte d’envoi n’est associé à cette fiche. Envoyez ce message depuis la boîte unifiée.');
      return;
    }
    envoyerModele(services.client, {
      personId: personne.id,
      dossierId: personne.dossierId,
      channelAccountId: compte,
      modele,
      corps,
      nomsMandant: verification === 'ok' ? nomsMandant : null,
    })
      .then((v) => {
        setAvertissement(v.ok ? null : v.message);
        setEnvoye(v.ok);
      })
      .catch((e: unknown) => setAvertissement(messageErreur(e)));
  };

  return (
    <VueFicheRapide
      fiche={personne ? construireFiche(personne) : FICHE_VIDE}
      modeles={modeles}
      choisi={choisi}
      corps={corps}
      avertissement={avertissement}
      envoye={envoye}
      verificationMandant={verification}
      onChoisir={choisir}
      onEnvoyer={envoyer}
      onCorps={setCorps}
      titre={ecran.titre}
    />
  );
};
